import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import Navbar, { BrandMark } from "@/components/Navbar";
import { ArrowRight, Sparkles, Users, DoorOpen, Wrench, GraduationCap, BookOpen, ChevronLeft, ChevronRight, Camera } from "lucide-react";
import api from "@/lib/api";

const EXAMPLES = [
  {
    tag: "Automotive",
    problem: "My mower shuts off when I engage the blades.",
    ai: "Sounds like a safety-switch or fuel-starvation issue. Check the seat interlock and the fuel filter first.",
    helper: { name: "Mike R.", role: "ASE Certified Tech", helped: 1284, initial: "M", color: "bg-amber-500" },
  },
  {
    tag: "Appliances",
    problem: "My dryer turns on but won't heat up.",
    ai: "Most common cause is a blown thermal fuse from a clogged vent. Check the vent line and fuse before ordering parts.",
    helper: { name: "Robert P.", role: "Appliance Repair · 22 yrs", helped: 872, initial: "R", color: "bg-teal-600" },
  },
  {
    tag: "Technology",
    problem: "My gaming PC keeps losing display mid-game.",
    ai: "Likely a GPU thermal-throttle or cable/driver issue. Check case airflow and swap the DisplayPort cable.",
    helper: { name: "Lena T.", role: "Full-stack + hardware", helped: 341, initial: "L", color: "bg-blue-500" },
  },
  {
    tag: "Plumbing",
    problem: "My sink keeps dripping even after I replaced the washer.",
    ai: "The valve seat is probably worn. Replacing the washer alone won't help if the seat is scored.",
    helper: { name: "Diane K.", role: "Home Repair · 12 yrs", helped: 512, initial: "D", color: "bg-emerald-500" },
  },
  {
    tag: "Cooking",
    problem: "My bread keeps coming out too dense.",
    ai: "Underproofing or too little hydration. Try 78% water and a longer bulk ferment before shaping.",
    helper: { name: "Sam O.", role: "Home baker · sourdough", helped: 128, initial: "S", color: "bg-orange-500" },
  },
];

export default function Landing() {
  const [i, setI] = useState(0);
  const [paused, setPaused] = useState(false);
  const [activity, setActivity] = useState(null);

  useEffect(() => {
    api.get("/activity/live").then(({ data }) => setActivity(data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (paused) return;
    const t = setInterval(() => setI((n) => (n + 1) % EXAMPLES.length), 6000);
    return () => clearInterval(t);
  }, [paused]);

  const ex = EXAMPLES[i];

  return (
    <div className="min-h-screen warm-grain">
      <Navbar />

      <section className="max-w-7xl mx-auto px-4 sm:px-6 pt-12 sm:pt-16 pb-16 sm:pb-20">
        <div className="rise-in text-xs font-mono-label text-[color:hsl(var(--teal))] mb-4">
          <Sparkles className="inline w-3 h-3 mr-1" /> Connect · Learn · Interact · Network · Grow
        </div>

        <div className="grid lg:grid-cols-12 gap-8 lg:gap-10 items-start">
          <div className="lg:col-span-7">
            <h1 className="rise-in font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
              People + AI.<br/>
              <span className="text-[color:hsl(var(--teal))]">Better together</span>
              <span className="text-[color:hsl(var(--amber))]">.</span>
            </h1>
            <p className="rise-in-2 mt-5 text-lg text-muted-foreground max-w-xl leading-relaxed">
              Get immediate AI help, then connect with real people who've actually done it. CLING turns a question into a conversation with someone who knows.
            </p>

            <div className="rise-in-3 mt-7 flex flex-wrap gap-3">
              <Button asChild size="lg" className="btn-teal rounded-full px-7 h-12 text-base font-semibold" data-testid="hero-get-help">
                <Link to="/signup">Get Help <ArrowRight className="w-4 h-4 ml-1.5" /></Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="rounded-full px-6 h-12" data-testid="hero-become-helper">
                <Link to="/signup?intent=helper">Become a Helper</Link>
              </Button>
            </div>

            {activity?.has_activity ? (
              <LiveActivityStrip activity={activity} />
            ) : (
              <div className="mt-6 text-xs text-muted-foreground" data-testid="activity-muted">
                Live activity appears here once helpers are online.
              </div>
            )}

            <div className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3">
              {[
                { i: Wrench, label: "Fix something" },
                { i: BookOpen, label: "Learn something" },
                { i: GraduationCap, label: "Find a teacher" },
                { i: Users, label: "Find your people" },
              ].map(({ i: I, label }) => (
                <div key={label} className="border rounded-2xl bg-card px-3 py-2.5 flex items-center gap-2">
                  <I className="w-4 h-4 text-[color:hsl(var(--teal))] shrink-0" />
                  <span className="text-sm font-medium">{label}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="lg:col-span-5 rise-in-3">
            <DynamicExampleCard
              ex={ex}
              index={i}
              total={EXAMPLES.length}
              onPrev={() => { setPaused(true); setI((n) => (n - 1 + EXAMPLES.length) % EXAMPLES.length); }}
              onNext={() => { setPaused(true); setI((n) => (n + 1) % EXAMPLES.length); }}
              onSelect={(n) => { setPaused(true); setI(n); }}
              onPause={setPaused}
            />
          </div>
        </div>
      </section>

      <section id="how" className="border-t bg-secondary/40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16 sm:py-20">
          <div className="text-xs font-mono-label text-[color:hsl(var(--amber))] mb-3">How CLING works</div>
          <h2 className="font-display font-bold text-3xl sm:text-4xl max-w-2xl">Describe it. AI helps. Someone experienced steps inside.</h2>
          <div className="mt-10 grid md:grid-cols-3 gap-6">
            {[
              { i: Camera, t: "1. Describe it", d: "Type, speak, or snap a photo. CLING understands the problem — no need to pick a category." },
              { i: Sparkles, t: "2. AI gets you started", d: "You get an AI response immediately, while CLING finds humans with real experience in that exact topic." },
              { i: DoorOpen, t: "3. You pick who joins", d: "Interested helpers enter your Helper Queue. Compare profiles and invite the person you trust." },
            ].map(({ i: I, t, d }) => (
              <div key={t} className="border rounded-2xl bg-card p-6">
                <I className="w-6 h-6 text-[color:hsl(var(--teal))]" />
                <h3 className="mt-4 font-display font-bold text-xl">{t}</h3>
                <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="helpers" className="max-w-7xl mx-auto px-4 sm:px-6 py-16 sm:py-20">
        <div className="grid md:grid-cols-2 gap-10 items-center">
          <div>
            <div className="text-xs font-mono-label text-[color:hsl(var(--teal))] mb-3">Why become a helper</div>
            <h2 className="font-display font-bold text-3xl sm:text-4xl">Real people. Real experience. Real thanks.</h2>
            <p className="mt-4 text-muted-foreground">
              Someone out there knows how to solve exactly what you've solved. CLING pairs you with them — and preserves what you shared for the next person. Free community help remains the heart of CLING; Kudos and paid dedicated sessions are optional.
            </p>
            <div className="mt-5 flex flex-wrap gap-3">
              <Button asChild className="btn-amber rounded-full h-11 px-5" data-testid="cta-become-helper">
                <Link to="/signup?intent=helper">Become a Helper</Link>
              </Button>
              <Button asChild variant="outline" className="rounded-full h-11 px-5">
                <Link to="/login">I already have an account</Link>
              </Button>
            </div>
          </div>
          <div className="border rounded-3xl bg-card p-6">
            <div className="grid grid-cols-4 gap-3 text-xs font-mono-label text-muted-foreground">
              <div>I KNOW</div><div>I CAN TEACH</div><div>I'M LEARNING</div><div>I NEED HELP WITH</div>
            </div>
            <div className="mt-3 grid grid-cols-4 gap-2 sm:gap-3 text-xs sm:text-sm">
              {["Diesel engines","Basic maintenance","Welding","Cabinet building",
                "Sourdough","Bread basics","Espresso pulls","Overproofing",
                "React","Beginner web","Rust","Auth flows",
                "Machining","Lathe tips","CAM software","Fixture design"].map((s, idx) => (
                <div key={idx} className={`rounded-xl border px-2.5 py-1.5 ${idx%4===0?"bg-[color:hsl(var(--teal))]/10 border-[color:hsl(var(--teal))]/30": idx%4===1?"bg-[color:hsl(var(--amber))]/10 border-[color:hsl(var(--amber))]/30":"bg-secondary"}`}>
                  {s}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <footer className="border-t">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8 flex flex-wrap items-center justify-between gap-4">
          <BrandMark />
          <div className="flex items-center gap-4 text-xs">
            <Link
              to="/legal"
              data-testid="footer-legal-link"
              className="text-muted-foreground hover:text-[color:hsl(var(--teal))] transition-colors"
            >
              Legal
              <span className="ml-1.5 rounded px-1 py-0.5 font-mono-label text-[9px] uppercase tracking-wider text-[color:hsl(var(--amber))] bg-[color:hsl(var(--amber))]/10">
                Draft
              </span>
            </Link>
            <p className="text-muted-foreground">Connect · Learn · Interact · Network · Grow — © CLING</p>
          </div>
        </div>
      </footer>
    </div>
  );
}

function LiveActivityStrip({ activity }) {
  return (
    <div className="mt-6 inline-flex flex-wrap items-center gap-3 rounded-full border bg-card px-4 py-2 text-xs" data-testid="live-activity">
      <span className="relative flex h-2 w-2">
        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
        <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
      </span>
      <span><b className="font-display">{activity.online_helpers}</b> helpers online</span>
      <span className="opacity-40">·</span>
      <span><b className="font-display">{activity.active_rooms}</b> active room{activity.active_rooms === 1 ? "" : "s"}</span>
      {activity.people_getting_help > 0 && (
        <>
          <span className="opacity-40">·</span>
          <span><b className="font-display">{activity.people_getting_help}</b> in a Help Room now</span>
        </>
      )}
    </div>
  );
}

function DynamicExampleCard({ ex, index, total, onPrev, onNext, onSelect, onPause }) {
  return (
    <div
      className="relative border rounded-3xl bg-card p-5 sm:p-6 shadow-xl overflow-hidden"
      onMouseEnter={() => onPause?.(true)}
      onMouseLeave={() => onPause?.(false)}
      data-testid="dynamic-example-card"
    >
      <div className="absolute -top-24 -right-24 w-64 h-64 rounded-full bg-[color:hsl(var(--amber))]/10 blur-3xl pointer-events-none" />
      <div className="flex items-center justify-between text-xs font-mono-label text-[color:hsl(var(--teal))]">
        <span data-testid="example-tag">Example · {ex.tag}</span>
        <div className="flex items-center gap-1">
          <button onClick={onPrev} className="p-1 rounded-full hover:bg-secondary" aria-label="Previous example" data-testid="example-prev"><ChevronLeft className="w-4 h-4" /></button>
          <button onClick={onNext} className="p-1 rounded-full hover:bg-secondary" aria-label="Next example" data-testid="example-next"><ChevronRight className="w-4 h-4" /></button>
        </div>
      </div>
      <div className="mt-3 font-display font-bold text-lg leading-snug" data-testid="example-problem">{ex.problem}</div>
      <div className="mt-4 space-y-3">
        <div className="flex gap-2 items-start">
          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[color:hsl(var(--teal))] to-[color:hsl(var(--amber))] text-white text-xs grid place-items-center font-semibold shrink-0">AI</div>
          <div className="ai-bubble rounded-2xl rounded-tl-sm px-4 py-2 text-sm max-w-[85%]">
            <div className="font-mono-label text-[10px] text-[color:hsl(var(--teal))] mb-1">CLING AI</div>
            {ex.ai}
          </div>
        </div>
        <div className="flex gap-2 items-center pt-1">
          <div className={`w-9 h-9 rounded-full ${ex.helper.color} text-white text-xs grid place-items-center font-semibold shrink-0`}>{ex.helper.initial}</div>
          <div className="min-w-0 flex-1">
            <div className="text-sm font-semibold truncate">{ex.helper.name}</div>
            <div className="text-[11px] text-muted-foreground truncate">{ex.helper.role} · {ex.helper.helped.toLocaleString()} helped</div>
          </div>
          <div className="text-[10px] font-mono-label text-emerald-600 dark:text-emerald-400 inline-flex items-center gap-1">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" /> Available
          </div>
        </div>
      </div>
      <div className="mt-5 rounded-2xl border-2 border-[color:hsl(var(--amber))]/50 bg-[color:hsl(var(--amber))]/10 p-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <DoorOpen className="w-5 h-5 text-[color:hsl(var(--amber))]" />
          <span className="text-sm font-semibold">Ready when you are</span>
        </div>
        <span className="text-xs font-mono-label text-[color:hsl(var(--amber))]">Invite to Help Room</span>
      </div>
      <div className="mt-4 flex items-center justify-center gap-1.5">
        {Array.from({ length: total }).map((_, n) => (
          <button
            key={n}
            onClick={() => onSelect(n)}
            aria-label={`Example ${n + 1}`}
            className={`h-1.5 rounded-full transition-all ${n === index ? "w-6 bg-[color:hsl(var(--teal))]" : "w-1.5 bg-border"}`}
            data-testid={`example-dot-${n}`}
          />
        ))}
      </div>
    </div>
  );
}
